import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, TextField, CircularProgress,
  Grid, Chip, Tooltip,
} from '@mui/material';
import { useAuth } from '../components/AuthContext';
import { API_BASE_URL } from '../components/api.js';

// ─── Constantes ──────────────────────────────────────────────────────────────
const DIAS_CURTO  = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MESES_NOMES = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro',
];

// ─── Helpers de formatação ───────────────────────────────────────────────────
const cur  = (v) =>
  v == null ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const num  = (v, d = 0) => v == null ? '—' : Number(v).toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d });
const flt  = (v, d = 2) => v == null ? '—' : Number(v).toFixed(d);

// ─── Estilos de célula ───────────────────────────────────────────────────────
const HEADER_STYLE = {
  background: '#37474f', color: '#fff',
  padding: '8px 10px', border: '1px solid #263238',
  textAlign: 'center', whiteSpace: 'nowrap',
  fontSize: 12, fontWeight: 600, position: 'sticky', top: 0, zIndex: 1,
};
const CELL = (extra = {}) => ({
  padding: '5px 8px', border: '1px solid #e0e0e0',
  textAlign: 'center', fontSize: 12, whiteSpace: 'nowrap', ...extra,
});
const TOTAL_CELL = (extra = {}) => ({
  ...CELL({ background: '#eceff1', fontWeight: 700, ...extra }),
});
const GRAND_CELL = (extra = {}) => ({
  ...CELL({ background: '#cfd8dc', fontWeight: 700, ...extra }),
});

// ─── Configuração das colunas ─────────────────────────────────────────────────
// Colunas — width é a largura inicial em px (arrastável pelo usuário)
const COLUNAS_DEF = [
  { key: 'dia_semana',     label: 'Dia',           width: 55  },
  { key: 'data',           label: 'Data',          width: 70  },
  { key: 'venda_real',     label: 'Venda Líq.',    width: 110 },
  { key: 'meta_franquia',  label: 'Meta Franq.',   width: 110 },
  { key: 'meta_loja',      label: 'Meta Loja',     width: 110 },
  { key: 'venda_prev',     label: 'Esperado',      width: 110 },
  { key: 'diferenca',      label: 'Diferença',     width: 110 },
  { key: 'esperado_total', label: 'Esp. Total',    width: 110 },
  { key: 'paes_real',      label: 'Pães Real',     width: 80  },
  { key: 'paes_prev',      label: 'Pães Prev.',    width: 80  },
  { key: 'vl_pao_real',    label: 'VL/Pão Real',  width: 90  },
  { key: 'vl_pao_prev',    label: 'VL/Pão Prev.', width: 90  },
];

// ─── Renderizador de célula ───────────────────────────────────────────────────
function renderCelula(key, linha) {
  const v = linha[key];

  switch (key) {
    case 'dia_semana':
      return DIAS_CURTO[linha.dia_semana] ?? '—';

    case 'data': {
      const [, , d] = (linha.data || '').split('-');
      return d || '—';
    }

    case 'paes_real':
    case 'paes_prev':
      return v == null ? '—' : num(Math.round(v));

    case 'vl_pao_real':
    case 'vl_pao_prev':
      return v == null ? '—' : `R$ ${flt(v)}`;

    case 'venda_real':
    case 'meta_franquia':
    case 'meta_loja':
    case 'venda_prev':
    case 'esperado_total':
      return cur(v);

    case 'diferenca':
      if (v == null) return '—';
      return (
        <span style={{ color: v >= 0 ? '#2e7d32' : '#c62828', fontWeight: 600 }}>
          {cur(v)}
        </span>
      );

    default:
      return v ?? '—';
  }
}

// ─── Cor de linha ─────────────────────────────────────────────────────────────
function bgLinha(linha, hover) {
  if (hover)          return '#e3f2fd';
  if (linha.tem_real) return '#ffffff';
  return '#fffde7';   // dias futuros — fundo levemente amarelo
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function PreverMetas() {
  const { authenticatedFetch } = useAuth();

  const hoje = new Date();
  const [mes,      setMes]      = useState(hoje.getMonth() + 1);
  const [ano,      setAno]      = useState(hoje.getFullYear());
  const [loading,  setLoading]  = useState(false);
  const [dados,    setDados]    = useState(null);         // resposta completa da API
  const [metaF,    setMetaF]    = useState('');
  const [metaL,    setMetaL]    = useState('');
  const [hovRow,   setHovRow]   = useState(null);
  const [salvando, setSalvando] = useState(false);

  // ── Larguras das colunas (redimensionável) ──────────────────────────────────
  const [colWidths, setColWidths] = useState(COLUNAS_DEF);
  const resizeRef = useRef(null);   // { idx, startX, startW }

  const onResizeStart = useCallback((e, idx) => {
    e.preventDefault();
    resizeRef.current = { idx, startX: e.clientX, startW: colWidths[idx].width };

    const onMove = (ev) => {
      const delta = ev.clientX - resizeRef.current.startX;
      const newW  = Math.max(40, resizeRef.current.startW + delta);
      setColWidths((prev) =>
        prev.map((c, i) => i === idx ? { ...c, width: newW } : c)
      );
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
  }, [colWidths]);

  // ── Busca dados ─────────────────────────────────────────────────────────────
  const fetchDados = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authenticatedFetch(
        `${API_BASE_URL}/api/prever-metas-comparativo/?mes=${mes}&ano=${ano}`
      );
      if (res.ok) {
        const d = await res.json();
        setDados(d);
        setMetaF(d.meta_franquia || '');
        setMetaL(d.meta_loja     || '');
      }
    } finally {
      setLoading(false);
    }
  }, [mes, ano]);

  useEffect(() => { fetchDados(); }, [fetchDados]);

  // ── Salva metas (onBlur) ────────────────────────────────────────────────────
  const salvarMetas = async () => {
    setSalvando(true);
    try {
      await authenticatedFetch(
        `${API_BASE_URL}/api/prever-metas-comparativo/?mes=${mes}&ano=${ano}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ meta_franquia: Number(metaF), meta_loja: Number(metaL) }),
        }
      );
      await fetchDados();   // recarrega com a nova distribuição
    } finally {
      setSalvando(false);
    }
  };

  const linhas = dados?.linhas  ?? [];
  const tot    = dados?.totais  ?? {};

  // ─── Renderização ─────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 2 }}>

      {/* Cabeçalho ─────────────────────────────────────────────────────────── */}
      <Box sx={{ maxWidth: 800, display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6" fontWeight="bold">
          Acompanhamento de Metas
        </Typography>

        {/* Seletor Mês */}
        <TextField
          select size="small" label="Mês" value={mes}
          onChange={(e) => setMes(Number(e.target.value))}
          SelectProps={{ native: true }}
          sx={{ minWidth: 130 }}
        >
          {MESES_NOMES.map((m, i) => (
            <option key={i + 1} value={i + 1}>{m}</option>
          ))}
        </TextField>

        {/* Seletor Ano */}
        <TextField
          select size="small" label="Ano" value={ano}
          onChange={(e) => setAno(Number(e.target.value))}
          SelectProps={{ native: true }}
          sx={{ minWidth: 90 }}
        >
          {[2024, 2025, 2026].map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </TextField>
      </Box>

      {/* Campos de Meta ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          maxWidth: 800,
          display: 'flex', gap: 2, mb: 2, p: 2,
          bgcolor: '#f5f5f5', borderRadius: 2, flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <TextField
          size="small" label="Meta Franquia (mensal)" type="number"
          value={metaF}
          onChange={(e) => setMetaF(e.target.value)}
          onBlur={salvarMetas}
          sx={{ width: 200 }}
          InputProps={{ startAdornment: <span style={{ marginRight: 4, color: '#888' }}>R$</span> }}
        />
        <TextField
          size="small" label="Meta Loja (mensal)" type="number"
          value={metaL}
          onChange={(e) => setMetaL(e.target.value)}
          onBlur={salvarMetas}
          sx={{ width: 200 }}
          InputProps={{ startAdornment: <span style={{ marginRight: 4, color: '#888' }}>R$</span> }}
        />
        {salvando && <CircularProgress size={20} />}

        {/* Legenda */}
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Chip size="small" label="Realizado"  sx={{ bgcolor: '#fff',    border: '1px solid #bbb', fontSize: 11 }} />
          <Chip size="small" label="Previsto"   sx={{ bgcolor: '#fffde7', border: '1px solid #bbb', fontSize: 11 }} />
        </Box>
      </Box>

      {/* Tabela ─────────────────────────────────────────────────────────────── */}
      <Box sx={{ maxWidth: 1000, border: '1px solid #cfd8dc', borderRadius: 1, overflow: 'auto', maxHeight: '100%' }}>
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : linhas.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: '#999' }}>
            Nenhum dado para {MESES_NOMES[mes - 1]}/{ano}.
          </Box>
        ) : (
          <table style={{ borderCollapse: 'collapse', width: '100%', fontFamily: 'sans-serif' }}>
            <thead>
              <tr>
                {colWidths.map((col, idx) => (
                  <th key={col.key} style={{ ...HEADER_STYLE, width: col.width, position: 'relative', userSelect: 'none' }}>
                    {col.label}
                    {/* Alça de redimensionamento */}
                    <span
                      onMouseDown={(e) => onResizeStart(e, idx)}
                      style={{
                        position: 'absolute', right: 0, top: 0, bottom: 0,
                        width: 6, cursor: 'col-resize',
                        background: 'rgba(255,255,255,0.15)',
                      }}
                      title="Arraste para redimensionar"
                    />
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {linhas.map((linha, idx) => (
                <tr
                  key={idx}
                  style={{ background: bgLinha(linha, hovRow === idx) }}
                  onMouseEnter={() => setHovRow(idx)}
                  onMouseLeave={() => setHovRow(null)}
                >
                  {colWidths.map((col) => (
                    <td
                      key={col.key}
                      style={CELL({
                        fontWeight: col.key === 'venda_real' ? 600 : 'normal',
                        color:
                          col.key === 'dia_semana' && linha.dia_semana === 0
                            ? '#c62828'   // domingo em vermelho
                            : undefined,
                      })}
                    >
                      {renderCelula(col.key, linha)}
                    </td>
                  ))}
                </tr>
              ))}

              {/* ── Linha de TOTAIS — ordem igual às colunas ──────────────── */}
              <tr>
                <td colSpan={2} style={TOTAL_CELL({ textAlign: 'left', paddingLeft: 10 })}>
                  TOTAL
                </td>
                {/* venda_real */}
                <td style={TOTAL_CELL()}>{cur(tot.venda_real)}</td>
                {/* meta_franquia */}
                <td style={TOTAL_CELL()}>{cur(tot.meta_franquia)}</td>
                {/* meta_loja */}
                <td style={TOTAL_CELL()}>{cur(tot.meta_loja)}</td>
                {/* venda_prev / esperado */}
                <td style={TOTAL_CELL()}>{cur(tot.venda_prev)}</td>
                {/* diferenca */}
                <td style={TOTAL_CELL({ color: (tot.diferenca ?? 0) >= 0 ? '#2e7d32' : '#c62828' })}>
                  {cur(tot.diferenca)}
                </td>
                {/* esperado_total */}
                <td style={TOTAL_CELL()}>{cur(tot.esperado_total)}</td>
                {/* paes_real */}
                <td style={TOTAL_CELL()}>{num(tot.paes_real)}</td>
                {/* paes_prev */}
                <td style={TOTAL_CELL()}>{num(tot.paes_prev)}</td>
                {/* vl_pao — média não se soma */}
                <td style={TOTAL_CELL()}>—</td>
                <td style={TOTAL_CELL()}>—</td>
              </tr>

              {/* ── Linha META MENSAL (abaixo do total de meta franq/loja) ──── */}
              <tr>
                <td colSpan={2} style={TOTAL_CELL({ textAlign: 'left', paddingLeft: 10, color: '#1565c0' })}>
                  Meta Mensal
                </td>
                {/* venda_real — vazio */}
                <td style={TOTAL_CELL()}>—</td>
                {/* meta_franquia mensal */}
                <td style={TOTAL_CELL({ color: '#1565c0', fontWeight: 700 })}>
                  {cur(dados?.meta_franquia)}
                </td>
                {/* meta_loja mensal */}
                <td style={TOTAL_CELL({ color: '#1565c0', fontWeight: 700 })}>
                  {cur(dados?.meta_loja)}
                </td>
                {/* demais colunas — vazio */}
                <td style={TOTAL_CELL()}>—</td>
                <td style={TOTAL_CELL()}>—</td>
                <td style={TOTAL_CELL()}>—</td>
                <td style={TOTAL_CELL()}>—</td>
                <td style={TOTAL_CELL()}>—</td>
                <td style={TOTAL_CELL()}>—</td>
                <td style={TOTAL_CELL()}>—</td>
              </tr>

              {/* ── Linha TOTAL REAL + ESPERADO FUTURO ───────────────────────── */}
              <tr>
                <td
                  colSpan={10}
                  style={GRAND_CELL({ textAlign: 'right', paddingRight: 12 })}
                >
                  Total Realizado + Esperado Futuro →
                </td>
                <td colSpan={2} style={GRAND_CELL({ color: '#1565c0', fontSize: 13 })}>
                  {cur(tot.real_mais_prev)}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </Box>

      {/* Rodapé informativo ─────────────────────────────────────────────────── */}
      {!loading && linhas.length > 0 && (
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
          Meta distribuída proporcionalmente pelo peso do faturamento previsto de cada dia.
          Linhas amarelas = dias ainda sem venda real registrada.
        </Typography>
      )}
    </Box>
  );
}